'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { ShieldAlert, AlertTriangle, TrendingDown, CheckCircle, Clock, Zap, Eye, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const ALERTS = [
  { id: 'CA-2025-0891', taxpayer: 'Sinoe Timber Co.', tin: 'TIN-2024-022341', type: 'Under-reporting', severity: 'critical', score: 94, description: 'Revenue declared 68% below sector average for 3 consecutive quarters', county: 'Sinoe', detected: '2025-05-27' },
  { id: 'CA-2025-0887', taxpayer: 'James Kollie', tin: 'TIN-2024-014567', type: 'Ghost Transactions', severity: 'critical', score: 89, description: 'Multiple duplicate invoice submissions detected across 4 filing periods', county: 'Nimba', detected: '2025-05-26' },
  { id: 'CA-2025-0874', taxpayer: 'Margibi Industrial Corp.', tin: 'TIN-2024-031002', type: 'Evasion Pattern', severity: 'high', score: 76, description: 'Systematic splitting of transactions below reporting threshold', county: 'Margibi', detected: '2025-05-24' },
  { id: 'CA-2025-0861', taxpayer: 'LPA Holdings Ltd.', tin: 'TIN-2024-009034', type: 'Late Filing', severity: 'medium', score: 58, description: 'Quarterly returns filed 45+ days late for 2 consecutive periods', county: 'Grand Bassa', detected: '2025-05-22' },
  { id: 'CA-2025-0849', taxpayer: 'Fatima Kamara', tin: 'TIN-2024-025678', type: 'Discrepancy', severity: 'medium', score: 52, description: 'Bank deposit records exceed declared income by $18,400', county: 'Lofa', detected: '2025-05-20' },
  { id: 'CA-2025-0831', taxpayer: 'Mary Pewee', tin: 'TIN-2024-003291', type: 'Non-Filing', severity: 'low', score: 34, description: 'Annual return for FY2024 not submitted — 90 days overdue', county: 'Montserrado', detected: '2025-05-18' },
];

const severityConfig: Record<string, { color: string; bg: string; label: string }> = {
  critical: { color: '#f87171', bg: 'rgba(248,113,113,0.12)', label: 'Critical' },
  high: { color: '#fb923c', bg: 'rgba(251,146,60,0.12)', label: 'High' },
  medium: { color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', label: 'Medium' },
  low: { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', label: 'Low' },
};

const riskByCounty = [
  { county: 'Montserrado', score: 62 },
  { county: 'Nimba', score: 81 },
  { county: 'Bong', score: 44 },
  { county: 'Lofa', score: 57 },
  { county: 'Grand Bassa', score: 49 },
  { county: 'Margibi', score: 73 },
  { county: 'Sinoe', score: 88 },
  { county: 'Maryland', score: 38 },
];

const kpis = [
  { label: 'Active Alerts', value: '12', icon: AlertTriangle, color: '#f87171' },
  { label: 'Cases Resolved', value: '847', icon: CheckCircle, color: '#34d399' },
  { label: 'Avg Risk Score', value: '61.4', icon: TrendingDown, color: '#fbbf24' },
  { label: 'Pending Review', value: '34', icon: Clock, color: '#60a5fa' },
];

export default function ComplianceEngineClient() {
  const [severityFilter, setSeverityFilter] = useState('All');
  const [selectedAlert, setSelectedAlert] = useState<string | null>(null);

  const filtered = ALERTS.filter((a) => severityFilter === 'All' || a.severity === severityFilter.toLowerCase());
  const selected = ALERTS.find((a) => a.id === selectedAlert);

  return (
    <AppLayout>
      <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)' }}>
              <ShieldAlert size={20} style={{ color: '#070e1f' }} />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--dark-text)' }}>AI Compliance Engine</h1>
              <p className="text-xs sm:text-sm" style={{ color: 'var(--dark-muted)' }}>Real-time fraud detection · Risk scoring · Evasion pattern analysis</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}>
            <Zap size={12} />
            AI Engine Active
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {kpis.map((k) => {
            const KIcon = k.icon;
            return (
              <div key={k.label} className="rounded-xl px-4 py-4 flex items-center gap-3"
                style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${k.color}18` }}>
                  <KIcon size={18} style={{ color: k.color }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{k.label}</p>
                  <p className="text-lg font-bold font-mono-data" style={{ color: k.color }}>{k.value}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Alerts list */}
          <div className="lg:col-span-2 rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--dark-border)' }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Compliance Alerts</p>
              <div className="flex gap-1.5">
                {['All', 'Critical', 'High', 'Medium', 'Low'].map((s) => (
                  <button key={s} onClick={() => setSeverityFilter(s)}
                    className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                    style={{
                      backgroundColor: severityFilter === s ? 'rgba(212,160,23,0.15)' : 'var(--dark-elevated)',
                      color: severityFilter === s ? 'var(--accent)' : 'var(--dark-muted)',
                      border: `1px solid ${severityFilter === s ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                    }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="divide-y" style={{ borderColor: 'var(--dark-border)' }}>
              {filtered.map((alert) => {
                const sc = severityConfig[alert.severity];
                const isSelected = alert.id === selectedAlert;
                return (
                  <div key={alert.id} onClick={() => setSelectedAlert(alert.id === selectedAlert ? null : alert.id)}
                    className="px-4 py-3 cursor-pointer transition-colors"
                    style={{ backgroundColor: isSelected ? 'rgba(212,160,23,0.05)' : 'transparent' }}
                    onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.02)'; }}
                    onMouseLeave={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono-data font-semibold" style={{ color: 'var(--accent)' }}>{alert.id}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-muted)' }}>{alert.type}</span>
                        </div>
                        <p className="text-sm font-medium truncate" style={{ color: 'var(--dark-text)' }}>{alert.taxpayer}</p>
                        <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--dark-muted)' }}>{alert.description}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <div className="text-lg font-bold font-mono-data" style={{ color: alert.score > 75 ? '#f87171' : alert.score > 50 ? '#fbbf24' : '#60a5fa' }}>{alert.score}</div>
                        <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{alert.detected}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right panel */}
          <div className="flex flex-col gap-4">
            {/* Alert detail */}
            <div className="rounded-xl p-4 flex-1" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
              {selected ? (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle size={15} style={{ color: severityConfig[selected.severity].color }} />
                    <span className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>{selected.id}</span>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { label: 'Taxpayer', value: selected.taxpayer },
                      { label: 'TIN', value: selected.tin },
                      { label: 'Alert Type', value: selected.type },
                      { label: 'County', value: selected.county },
                      { label: 'Detected', value: selected.detected },
                    ].map((row) => (
                      <div key={row.label} className="flex justify-between py-1.5 border-b" style={{ borderColor: 'var(--dark-border)' }}>
                        <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{row.label}</span>
                        <span className="text-xs font-medium" style={{ color: 'var(--dark-text)' }}>{row.value}</span>
                      </div>
                    ))}
                    <div className="pt-1">
                      <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>AI Risk Score</p>
                      <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                        <div className="h-full rounded-full" style={{ width: `${selected.score}%`, backgroundColor: selected.score > 75 ? '#f87171' : '#fbbf24' }} />
                      </div>
                      <p className="text-xs text-right mt-0.5 font-bold" style={{ color: selected.score > 75 ? '#f87171' : '#fbbf24' }}>{selected.score}/100</p>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button className="flex-1 py-2 rounded-lg text-xs font-semibold" style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.3)' }}>
                        Open Case
                      </button>
                      <button className="flex-1 py-2 rounded-lg text-xs font-semibold" style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}>
                        Escalate
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-center">
                  <Eye size={28} className="mb-2" style={{ color: 'var(--dark-muted)' }} />
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Select an alert to view details</p>
                </div>
              )}
            </div>

            {/* County risk chart */}
            <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 size={14} style={{ color: 'var(--accent)' }} />
                <p className="text-xs font-semibold" style={{ color: 'var(--dark-text)' }}>Risk Score by County</p>
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={riskByCounty} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <XAxis dataKey="county" tick={{ fontSize: 9, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', borderRadius: '8px', fontSize: '11px' }} />
                  <Bar dataKey="score" radius={[3, 3, 0, 0]}>
                    {riskByCounty.map((entry) => (
                      <Cell key={entry.county} fill={entry.score > 70 ? '#f87171' : entry.score > 50 ? '#fbbf24' : '#34d399'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
